// #region Imports
import WorkVersion from "work/work-agreements/versions/work-version";
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { CheckboxProps, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import InputAdornment from "@mui/material/InputAdornment";
import { keyframes, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { visuallyHidden } from "@mui/utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions, { type RowOpenAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { usePersistedColumnVisibility } from "_shared/_views/tables/table-column-visibility-preference";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { debounce } from "lodash";
import { isSettledIndividual } from "marketplace/individuals/individual-resolution";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  createMRTColumnHelper,
  MRT_ActionMenuItem as ActionMenuItem,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { VIEWS_BUTTON_MAX_WIDTH, ViewsMenu } from "_shared/_views/list-pages/views-menu";
import {
  NARROW_TOOLBAR_MIN_WIDTH,
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageTabs,
  ListPageToolbarButtons,
} from "_shared/_views/list-pages/list-page-heading";
import ListChips from "_shared/_views/list-pages/list-chips";
import { proposalChipIcon } from "work/proposals/_views/proposal-move-swatch";
import { writeListChips, type ListChipCounts, type ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showActionableErrorSnackbar, showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import useSessionUser from "permissions/session/use-session-user";
import CreateFromPreviousProposalDialog from "work/proposals/drafts-from-previous/_views/create-from-previous-proposal-dialog";
import CreateProposalSplitButton from "work/proposals/_views/_buttons/create-proposal-split-button";
import { useGetProposalTemplatesQuery } from "work/proposal-templates/_api/proposal-template-api";
import CreateFromProposalTemplateDialog from "work/proposal-templates/_views/create-from-proposal-template-dialog";
import ProposalTemplatesTable, { usableTemplates } from "work/proposal-templates/_views/proposal-templates-table";
import { MouseEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { useSession } from "users/__session/session-context";
import FeeScheduleLink from "work/fee-schedule-templates/_views/links/fee-schedule-template-link";
import OnboardingProposalStatePage from "work/proposals/_views/onboarding/onboarding-proposal-state-page";
import {
  ProposalOrderField,
  ProposalStage,
} from "work/proposals/__constants/proposal-lifecycle";
import {
  useBulkDeleteProposalsMutation,
  useGetProposalsQuery,
  useProposalListCounts,
} from "work/proposals/_api/proposal-api";
import {
  DraftDeletionRefusal,
  DraftDeletionResultJSON,
  draftDeletionRefusalLabel,
  draftDeletionRefusalSummary,
  draftDeletionWithheldReason,
  draftSelectionOutsideDraftsReason,
} from "work/proposals/draft-deletion/draft-deletion-result";
import { Masked } from "work/masked-fields";
import WorkRecordCriteria from "work/work-records/work-record-criteria";
import WorkRecordCriteriaChips from "work/work-records/_views/work-record-criteria-chips";
import ProposalQuery, {
  GROUP_BY_REQUEST_PARAMETER,
  LEGACY_NEGOTIABLE_PARAMETER,
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import {
  getLastSuccessfulProposalQueryByInterface,
  getProposalQueryByInterface,
  getProposalsByQuery,
  isProposalLoadIssue,
  ProposalLoadIssue,
  updateProposalInterfaceQuery
} from "work/proposals/_store/proposals-redux-slice";
import CreatorLink from "work/proposals/_views/_links/creator-link/creator-link";
import { getProposalTableContext } from "work/proposals/_views/proposal-table-context";
import { getProposalTableContextRowStyles } from "work/proposals/_views/proposal-table-context-style";
import {
  datesColumnMinSize,
  proposalColumnSizes,
  statusColumnWidth,
  teamColumnSizes,
  teamColumnWidth,
  teamDisplayModeFor,
  hiddenUntilChosen,
  proposalColumnFolds,
  proposalColumnOrder,
} from "work/proposals/_views/proposal-table-columns";
import { proposalDateSorts, summarizeProposalDates } from "work/proposals/_views/proposal-table-dates";
import { proposalListDestination } from "work/proposals/_views/proposal-list-destination";
import ProposalsYouLeft from "work/proposals/_views/archive/proposals-you-left";
import {
  apiOutcomes,
  isArchiveAlone,
  listsArchive,
  MOVE_PARAMETER,
  OUTCOME_PARAMETER,
  ProposalOutcomeChip,
  isPageTab,
  PageTab,
  pageTabPath,
  proposalChipGroups,
  proposalListFilter,
  ProposalStageChip,
  readProposalChips,
  STAGE_PARAMETER,
  keepProposalsView,
} from "work/proposals/_views/proposals-page-tabs";
import Proposal from "work/proposals/proposal";
import ProposalComparisonLink from "work/proposals/comparison/_views/proposal-comparison-link";
import useProposalComparisonSizes from "work/proposals/comparison/_views/use-proposal-comparison-sizes";
import ProposalRequestDrawer from "work/proposals/comparison/_views/proposal-request-drawer";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { DateCell } from "_shared/_views/tables/date-cells";
import { StatusChip } from "_shared/_views/tables/status-chip";
import { proposalStatusTone } from "work/proposals/_views/proposal-status-tone";
import { formatDate } from "_shared/formatting/dates";
// #endregion

// #region Styled Components
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  display: "block",
  flex: "1 1 auto",
  fontSize: "1.1em",
  fontWeight: "600",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
// The name and the version beside it (#6636). The name keeps at least a few words; when the column
// cannot seat both, the version wraps under it rather than squeezing the name to an ellipsis.
const NameLine = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
  minWidth: 0,
  width: "100%",
  "& > :not(.MuiChip-root)": {
    flex: "1 1 12ch",
    minWidth: 0,
  },
  "& > .MuiChip-root": {
    flex: "0 0 auto",
  },
}));

// A cell's main content with a line under it, one above the other rather than side by side.
const StackedCell = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  minWidth: 0,
  width: "100%",
}));
// A term's two sides, which wrap after the dash rather than clip. Flex wrapping does not depend on
// `white-space`, which a compact cell sets to `nowrap`; each side keeps its own date on one line.
const TermSides = styled("span")(() => ({
  columnGap: "0.3em",
  display: "flex",
  flexWrap: "wrap",
  whiteSpace: "nowrap",
}));
const NonNegotiableChip = styled(Chip)(() => ({
  fontSize: "0.7rem",
  height: 20,
}));
const EllipsisSpan = styled("span")(() => ({
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const failedOperationKeyFrame = keyframes`
  0% { background-color: rgba(255, 100, 100, 0.1); }
  50% { background-color: rgba(255, 100, 100, 0.3); }
  100% { background-color: rgba(255, 100, 100, 0.1); }
`;
// #endregion

const columnHelper = createMRTColumnHelper<Proposal | ProposalLoadIssue>();

/**
 * What a header shows where it is not the column's name (#6600). The name stays the column's
 * `header`, which Show/Hide columns lists and a screen reader announces.
 */
const compactHeaderLabels: Readonly<Partial<Record<string, string>>> = { negotiable: "Neg." };

/** What the list holds under each stage chip, as its empty state and search box name it. */
const listSubjects: Record<string, string> = {
  [ProposalStageChip.Drafts]: "draft proposals",
  [ProposalStageChip.Active]: "active proposals",
  [ProposalStageChip.Archived]: "archived proposals",
};

/** What the list holds, by the stage chips that are on: no stage is the open list, and several are proposals. */
function listSubjectOf(stages: readonly string[]): string {
  if (stages.length === 0) return "open proposals";
  return stages.length === 1 ? listSubjects[stages[0]] : "proposals";
}

/** A restricted onboarding session's chips: none, whatever the URL says. */
const NO_CHIPS: ListChipSelection = {};

type SortingState = Array<{ id: string; desc: boolean }>;
type ProposalTableSearchState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

/**
 * The columns the list shows for a Show/hide columns choice: the reader's choice over the columns that
 * start hidden, with the columns this reader or this stage cannot use forced off.
 */
function proposalsColumnVisibility(
  choice: Readonly<Record<string, boolean>>,
  canGroupByRequest: boolean,
  hasArchivedRows: boolean,
): Record<string, boolean> {
  const visibility = { ...choice };
  for (const id of hiddenUntilChosen) visibility[id] = choice[id] ?? false;
  // Only the client side receives an RFPId, so a vendor's column would be empty on every row.
  visibility.rfp = canGroupByRequest ? (choice.rfp ?? true) : false;
  visibility.hireDate = hasArchivedRows ? (choice.hireDate ?? true) : false;
  visibility.deleted = hasArchivedRows ? (choice.deleted ?? true) : false;
  return visibility;
}

export default function ProposalsPage() {
  // #region Hooks
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionUser = useSessionUser();
  const location = useLocation();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  // The window's breakpoints stand in for the table's own width only until it has been measured.
  const isBelowXlViewport = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isBelowLgViewport = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const unmeasuredTeamColumnSize =
    teamColumnSizes[isBelowLgViewport ? "minimal" : isBelowXlViewport ? "compact" : "full"];
  const [userColumnVisibility, setUserColumnVisibility] = usePersistedColumnVisibility("proposals");
  // The view the reader left the list in, kept on their account (#6714): a plain visit to
  // `/proposals` opens their starred view or that one, and a link is always shown as it says.
  const tableViews = useCurrentTableView({
    tableKey: "proposals",
    basePath: "/proposals",
    keepWhatStillExists: keepProposalsView,
    disabled: session.isRestrictedOnboarding,
  });
  const { remember } = tableViews;

  // #region Route
  const pageTab = isPageTab(pageTabParam) ? pageTabParam : undefined;
  const effectivePageTab = pageTab ?? PageTab.Proposals;
  // The Templates tab is a different table (#6261): it lists the reader's saved templates.
  const isTemplatesTab = effectivePageTab === PageTab.Templates;

  // #region Chips
  // Which proposals the Proposals tab lists (#6473, #6712): stage chips that widen the list, move chips
  // that narrow it, and in the archive an outcome. No stage chip is every proposal that is not
  // archived. A restricted onboarding session has one proposal and no chips. Memoised on the URL
  // because the list's request is built from it in an effect, which must not run on every render.
  const chipsKey = searchParams.toString();
  const chips = useMemo(
    () => (session.isRestrictedOnboarding ? NO_CHIPS : readProposalChips(new URLSearchParams(chipsKey))),
    [chipsKey, session.isRestrictedOnboarding],
  );
  const stages = chips[STAGE_PARAMETER] ?? [];
  const isArchivedView = isArchiveAlone(chips);
  // Active with Archived still lists archived rows, so their dates stay on offer.
  const hasArchivedRows = listsArchive(chips);
  const isDraftsView = stages.length === 1 && stages[0] === ProposalStageChip.Drafts;
  const listSubject = listSubjectOf(stages);

  // Each chip's count, and the Proposals tab's: every open proposal, whatever the chips say. One
  // request answers them all (#6709), under the list's own criteria, so a column filter or a search
  // narrows the chips as it narrows the rows. A chip counts what the list shows with it chosen.
  const skipChipCounts = session.isRestrictedOnboarding;
  // The order goes too: a field the list sorts on that the reader may not read leaves a proposal out
  // of the list, so it leaves it out of the counts.
  const countsCriteria = new URLSearchParams([
    ...ProposalFilterParameters.fromSearchParams(
      searchParams,
      session,
      proposalListFilter(chips),
    ).searchParams,
    ...ProposalOrderParameters.fromSearchParams(searchParams).searchParams,
  ]).toString();
  const listCounts = useProposalListCounts({ criteria: countsCriteria }, { skip: skipChipCounts });
  const counts = listCounts.counts;
  // Memoised because the toolbar's callback depends on it: rebuilt on every render, it would rebuild
  // the toolbar on every render too, and that must not depend on the compiler noticing.
  const chipCounts = useMemo((): ListChipCounts => ({
    [STAGE_PARAMETER]: {
      [ProposalStageChip.Drafts]: counts?.stages[ProposalStage.Draft],
      [ProposalStageChip.Active]: counts?.stages[ProposalStage.Active],
      [ProposalStageChip.Archived]: counts?.stages[ProposalStage.Archived],
    },
    [MOVE_PARAMETER]: counts?.moves ?? {},
    [OUTCOME_PARAMETER]: {
      [ProposalOutcomeChip.Hired]: counts?.outcomes[apiOutcomes[ProposalOutcomeChip.Hired]],
      [ProposalOutcomeChip.Canceled]: counts?.outcomes[apiOutcomes[ProposalOutcomeChip.Canceled]],
      [ProposalOutcomeChip.AwardedElsewhere]: counts?.outcomes[apiOutcomes[ProposalOutcomeChip.AwardedElsewhere]],
    },
  }), [counts]);
  // #endregion

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  // Drafting a proposal needs Read/Write access, a higher bar than the read access that gets
  // someone onto this page. Saying so on the button beats letting them build a whole proposal and
  // meet the API's 403 at Save. Sending it is higher again, entity-wide, and the draft page says
  // so on its Submit button (#5795).
  // The super-user exclusion that used to sit on each button has moved inside `canCreateProposals`,
  // where it is stated once and explained: this view asks what the session may do, not who it is.
  const canCreateProposal = sessionUser?.canCreateProposals() ?? false;
  const [isCreateFromPreviousOpen, setIsCreateFromPreviousOpen] = useState(false);
  const [isCreateFromTemplateOpen, setIsCreateFromTemplateOpen] = useState(false);
  const createProposalTooltip = !sessionPermissions && !sessionPermissionsError
    ? "Loading proposal permissions..."
    : canCreateProposal
      ? "Create Proposal"
      : "Creating a proposal requires read/write proposal access. Ask an administrator at your firm.";
  // #endregion

  const interfaceName = `proposalsPage:${effectivePageTab}`;
  const proposalsQueryParams = useSelector(getProposalQueryByInterface(interfaceName));

  const paginatedProposalsJson = useSelector(getProposalsByQuery(proposalsQueryParams));
  const paginatedProposals = useMemo(() => {
    if (!paginatedProposalsJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const proposalJson of paginatedProposalsJson.data) {
      try {
        if (isProposalLoadIssue(proposalJson)) {
          deserializedData.push(proposalJson);
        } else {
          const proposal = Proposal.fromJSON({
            json: proposalJson,
            currentUser: session.user!,
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", proposalJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in proposalJson ? (Guid.fromJSON(proposalJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedProposalsJson, session.user]);

  // The Templates tab lists templates, so it asks for no proposals.
  const proposalsAPIQuery = useGetProposalsQuery(
    proposalsQueryParams && !isTemplatesTab
      ? { query: proposalsQueryParams, interfaceName }
      : skipToken,
  );
  // The same request the Templates tab's table makes, so its badge counts the rows that tab shows.
  const proposalTemplatesQuery = useGetProposalTemplatesQuery({}, { skip: session.isRestrictedOnboarding });
  const templateCount = proposalTemplatesQuery.data
    ? usableTemplates(proposalTemplatesQuery.data, session).length
    : undefined;

  // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs
  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulProposalQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isProposalLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const proposal = Proposal.fromJSON({
            json: item,
            currentUser: session.user!,
            options: { withRedline: false },
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson, session.user]);

  const tableProposals = paginatedProposals ?? lastSuccessfulQueryResponse;
  const tableProposalsHeaders = paginatedProposalsJson?.headers ?? lastSuccessfulQueryResponseJson?.headers;

  // How many proposals share each row's request, read once for the page (#581). Only the client
  // side receives an RFPId, so a vendor's page asks about nothing and offers no comparison.
  const comparisonSizes = useProposalComparisonSizes(
    (tableProposals ?? []).map((row) => (isProposalLoadIssue(row) ? undefined : row.RFPId?.value)),
  );

  // #region Creator hydration
  // Every creator this page will name, asked about in one lookup.
  //
  // The Creator cell reads its name out of the Individuals store, so something has to put it there,
  // and it cannot be the cell: a page of rows would then put one `individuals-by-userids` request on
  // the wire per row. This is the same shape `proposal-client-team-view.tsx` hydrates a roster with
  // — derive the ids, subtract what the store already holds, ask only for the remainder — lifted to
  // the table, which is the only place that can see the whole page at once.
  //
  // The two cells that never name anybody stay out of the set: the caller's own rows read "You",
  // and a restricted-onboarding viewer sees "AttorneyHub" on every row, so asking for either would
  // spend a request on a name no cell can render.
  const creatorUserIds = useMemo(() => {
    const userIds = new Set<string>();
    if (session.isRestrictedOnboarding) return userIds;
    for (const row of tableProposals ?? []) {
      if (isProposalLoadIssue(row)) continue;
      // `creator` is optional at runtime for the same reason the cell below guards it, even though
      // the type says otherwise. A row whose JSON carries no creator actually fails deserialization
      // and arrives here as a load issue, so this is a guard rather than a reachable state.
      const creatorUserId = row.creator?.userId.value;
      if (!creatorUserId) continue;
      if (creatorUserId === session.user?.id?.value) continue;
      userIds.add(creatorUserId);
    }
    return userIds;
  }, [session.isRestrictedOnboarding, session.user?.id?.value, tableProposals]);
  const cachedCreators = useSelector(getIndividualsByUserIds(creatorUserIds));
  const missingCreatorUserIds = useMemo(() => {
    // Keyed on userId alone, exactly as the roster is: `byUserId` in individuals-redux-slice.ts
    // holds one row per user id, and a name is a personal attribute rather than a per-entity one.
    // Keying on the compound `userId:legalEntityId` pair would only manufacture misses whenever a
    // cached row's legalEntityId disagreed with the proposal membership's.
    // A contact counts as missing: sharing the proposal may since have widened it into a profile.
    const cachedUserIds = new Set(
      cachedCreators.filter(isSettledIndividual).map((individual) => individual.userId.value),
    );
    return new Set(Array.from(creatorUserIds).filter((userId) => !cachedUserIds.has(userId)));
  }, [cachedCreators, creatorUserIds]);
  useGetIndividualsByUserIdsQuery(missingCreatorUserIds.size > 0 ? { userIds: [...missingCreatorUserIds] } : skipToken);
  // #endregion

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const searchParamsKey = searchParams.toString();
  const queryGlobalFilter = getGlobalFilterFromSearchParams(searchParams);
  const [globalFilterDraft, setGlobalFilterDraft] = useState(() => ({
    searchParamsKey,
    value: queryGlobalFilter,
  }));
  const globalFilter =
    globalFilterDraft.searchParamsKey === searchParamsKey ? globalFilterDraft.value : queryGlobalFilter;
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);

  const [bulkDeleteProposalsQuery] = useBulkDeleteProposalsMutation();
  // The drafts a delete refused, by id, with the reason the server gave; the previous set marks
  // which rows are newly refused, so only those flash.
  const [prevRefusedDeletions, setPrevRefusedDeletions] = useState<RefusedDeletions>({});
  const [refusedDeletions, setRefusedDeletions] = useState<RefusedDeletions>({});

  useEffect(() => {
    if (!session.isRestrictedOnboarding) return;
    if (pageTab && pageTab !== PageTab.Proposals) {
      navigate("/proposals", { replace: true });
    }
  }, [navigate, pageTab, session.isRestrictedOnboarding]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;
    if (isTemplatesTab) return;

    const listFilter = proposalListFilter(chips);
    const filterParameters = ProposalFilterParameters.fromSearchParams(searchParams, session, listFilter);
    const orderParameters = ProposalOrderParameters.fromSearchParams(searchParams);

    const newQuery = new ProposalQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromListPageSearchParams(searchParams),
    });
    if (proposalsQueryParams && newQuery.isEqualTo(proposalsQueryParams)) return;
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [isTemplatesTab, chips, session.user?.legalEntityId.value, session, searchParams, proposalsQueryParams, dispatch, interfaceName]);

  const updateSearchParamsFromTableState = useCallback(
    (nextState: ProposalTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState, searchParams);
      if (newSearchParams.toString() === searchParams.toString()) return;
      remember(`${location.pathname}?${newSearchParams.toString()}`);
      setSearchParams(newSearchParams);
    },
    [location.pathname, remember, searchParams, setSearchParams],
  );

  const commitGlobalFilterSearchParams = useCallback(
    (nextGlobalFilter: string) => {
      setSearchParams((currentSearchParams) => {
        const newSearchParams = getSearchParamsFromTableState(
          {
            globalFilter: nextGlobalFilter,
            columnFilters: getColumnFiltersFromSearchParams(currentSearchParams),
            sorting: getSortingFromSearchParams(currentSearchParams),
            pagination: getPaginationFromSearchParams(currentSearchParams),
          },
          currentSearchParams,
        );

        if (newSearchParams.toString() === currentSearchParams.toString()) return currentSearchParams;
        // The search text is never part of a view, but the change is still the reader's own: told
        // as one, so clearing the search back to a bare `/proposals` does not open a stored view.
        remember(`${location.pathname}?${newSearchParams.toString()}`);
        return newSearchParams;
      });
    },
    [location.pathname, remember, setSearchParams],
  );

  const debouncedCommitGlobalFilterSearchParams = useMemo(
    () => debounce(commitGlobalFilterSearchParams, 350),
    [commitGlobalFilterSearchParams],
  );

  useEffect(() => {
    return () => debouncedCommitGlobalFilterSearchParams.cancel();
  }, [debouncedCommitGlobalFilterSearchParams]);

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      setGlobalFilterDraft((previousDraft) => {
        const previousGlobalFilter =
          previousDraft.searchParamsKey === searchParamsKey ? previousDraft.value : queryGlobalFilter;
        const nextGlobalFilter = applyUpdater(updater, previousGlobalFilter);
        debouncedCommitGlobalFilterSearchParams(nextGlobalFilter);
        return {
          searchParamsKey,
          value: nextGlobalFilter,
        };
      });
    },
    [debouncedCommitGlobalFilterSearchParams, queryGlobalFilter, searchParamsKey],
  );

  const handlePageTabChange = useCallback(
    (_event: SyntheticEvent, newTab: PageTab) => {
      const path = pageTabPath(newTab);
      remember(path);
      debouncedCommitGlobalFilterSearchParams.cancel();
      navigate(path);
    },
    [debouncedCommitGlobalFilterSearchParams, navigate, remember],
  );

  // A chip starts the list from its first page, and drops a row selection made for other rows. The
  // reducer has already dropped a move chip that cannot match anything in the new stages, and an
  // outcome chip once the archive is not the whole list (#6576). The stages are the view a plain visit
  // to `/proposals` reopens.
  const handleChipsChange = useCallback(
    (selection: ListChipSelection) => {
      setRowSelection({});
      setSearchParams((prev) => {
        const params = writeListChips(proposalChipGroups, selection, prev);
        params.set("pageIndex", "0");
        remember(`/proposals?${params.toString()}`);
        return params;
      });
    },
    [remember, setRowSelection, setSearchParams],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<SortingState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  useEffect(() => {
    if (proposalsAPIQuery.isError) {
      showErrorSnackbar(proposalsAPIQuery.error, { doing: "loading your proposals", operation: "proposals.list" });
    }
  }, [proposalsAPIQuery.isError, proposalsAPIQuery.error]);

  const handleBulkDraftProposalDelete = useCallback(async () => {
    setPrevRefusedDeletions(refusedDeletions);
    const selectedRowIdValues = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIdValues.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    const outcome = await deleteDrafts(bulkDeleteProposalsQuery, selectedRowIdValues);
    setRowSelection({});
    if (!outcome) return;

    if (outcome.unavailable.length > 0) {
      setRefusedDeletions({
        ...refusedDeletions,
        ...Object.fromEntries(outcome.unavailable.map(({ id, reason }) => [id, reason])),
      });
    }
    if (outcome.deleted.length === 0) {
      showActionableErrorSnackbar(draftDeletionRefusalSummary(outcome.unavailable));
      return;
    }
    if (outcome.unavailable.length > 0) {
      enqueueSnackbar(
        `${outcome.deleted.length} deleted. ${draftDeletionRefusalSummary(outcome.unavailable)}`,
        { variant: "warning" },
      );
    } else {
      enqueueSnackbar(`${outcome.deleted.length} Proposals deleted successfully`, { variant: "success" });
    }
    if (proposalsQueryParams) {
      dispatch(
        updateProposalInterfaceQuery({
          interfaceName: interfaceName,
          query: proposalsQueryParams,
        }),
      );
    }
  }, [
    bulkDeleteProposalsQuery,
    confirm,
    dispatch,
    interfaceName,
    proposalsQueryParams,
    refusedDeletions,
    rowSelection,
    setRowSelection,
  ]);

  // #region Column fit
  // Only the client side receives an RFPId, so a vendor has nothing to group and is not offered it.
  const canGroupByRequest = !session.context?.viewingAsVendor && !session.isRestrictedOnboarding;

  const columnVisibility = useMemo(
    () => proposalsColumnVisibility(userColumnVisibility, canGroupByRequest, hasArchivedRows),
    [canGroupByRequest, hasArchivedRows, userColumnVisibility],
  );
  // The columns with no choice made: what the Default view shows, so the Views menu can tell a
  // column choice that changes nothing from one that changes the list (#6717).
  const defaultColumnVisibility = useMemo(
    () => proposalsColumnVisibility({}, canGroupByRequest, hasArchivedRows),
    [canGroupByRequest, hasArchivedRows],
  );

  // The select column is drawn under every stage chip, disabled outside Drafts, so the table keeps
  // its layout when the chip changes (#6575). A restricted onboarding session has no selection.
  const hasSelectColumn = !session.isRestrictedOnboarding;
  const selectionWithheldReason =
    isDraftsView ? undefined : draftSelectionOutsideDraftsReason;
  // A hidden column's content is not lost: the creator and the description read under the name, and a
  // non-negotiable proposal says so under its status. Shown, each column has it to itself again.
  const isCreatorUnderName = columnVisibility.creator === false;
  const isDescriptionUnderName = columnVisibility.description === false;
  const isNegotiableUnderStatus = columnVisibility.negotiable === false;
  // #endregion

  const renderStatusColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue): ReactNode => {
      if (table.getState().isLoading) return;
      if (isProposalLoadIssue(proposal)) {
        return (
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "4000px",
                  whiteSpace: "normal",
                },
              },
            }}
            title={`Our staff has been notified. Reference id: ${proposal.id.value}`}
          >
            <EllipsisSpan>Error Loading Proposal</EllipsisSpan>
          </Tooltip>
        );
      }

      const displayStatus = proposal.displayStatus;
      const refusal = proposal.id ? refusedDeletions[proposal.id.value] : undefined;
      if (refusal) {
        return (
          <>
            <StatusChip label={displayStatus} tone={proposalStatusTone(displayStatus)} />
            <br />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "0.7rem",
                color: "error.main",
              }}
            >
              {draftDeletionRefusalLabel(refusal)}
            </Box>
          </>
        );
      }

      return <StatusChip label={displayStatus} tone={proposalStatusTone(displayStatus)} />;
    },
    [refusedDeletions],
  );

  const renderCreatorColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue) => {
      if (table.getState().isLoading) return;
      if ((isProposalLoadIssue(proposal) || !proposal.creator)) return <NoValueDash />;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) {
        return "You";
      }
      if (session.isRestrictedOnboarding) {
        return "AttorneyHub";
      }
      return <CreatorLink creator={proposal.creator} />;
    },
    [session.isRestrictedOnboarding, session.user?.id],
  );

  // The creator as the line under a name reads them, or as Created by reads when it folds there. Plain
  // text, since a line of values cannot hold the profile link the column draws.
  const creatorLabel = useCallback(
    (proposal: Proposal | ProposalLoadIssue): string | undefined => {
      if (isProposalLoadIssue(proposal) || !proposal.creator) return undefined;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) return "You";
      if (session.isRestrictedOnboarding) return "AttorneyHub";
      const creatorUserId = proposal.creator.userId;
      const individual = cachedCreators.find((candidate) => candidate.userId.isEqualTo(creatorUserId));
      return individual?.fullName || "Proposal creator";
    },
    [cachedCreators, session.isRestrictedOnboarding, session.user?.id],
  );

  // A team draws as much as its column has room for, and the fit decides how much room that is.
  const renderTeamColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue, width: number) => {
      if (table.getState().isLoading) return;
      if (isProposalLoadIssue(proposal) || !proposal.vendorTeam) return <NoValueDash />;
      if (session.isRestrictedOnboarding) return "AttorneyHub team";
      return (
        <VendorTeamLink
          displayMode={teamDisplayModeFor(width)}
          proposal={proposal}
          session={session}
        />
      );
    },
    [session],
  );

  const renderFeeScheduleColumn = useCallback((table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue) => {
    if (table.getState().isLoading) return;
    if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return <NoValueDash />;
    if (session.isRestrictedOnboarding) {
      const feeCount = Array.isArray(proposal.feeSchedule) ? proposal.feeSchedule.length : 0;
      return feeCount === 0 ? "Fee schedule" : `${feeCount} ${feeCount === 1 ? "Fee" : "Fees"}`;
    }
    return <FeeScheduleLink proposal={proposal} />;
  }, [session.isRestrictedOnboarding]);

  // #region Grouped by request
  // One row for each request sent to several firms (#6460). The server groups before it pages, so
  // the choice is a criterion in the URL, like the chips, and it starts the list from the first page.
  const isGroupedByRequest =
    canGroupByRequest && searchParams.get(GROUP_BY_REQUEST_PARAMETER)?.toLowerCase() === "true";
  const handleToggleGroupByRequest = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (isGroupedByRequest) params.delete(GROUP_BY_REQUEST_PARAMETER);
      else params.set(GROUP_BY_REQUEST_PARAMETER, "true");
      params.set("pageIndex", "0");
      remember(`/proposals?${params.toString()}`);
      return params;
    });
  }, [isGroupedByRequest, remember, setSearchParams]);
  const [openRequestId, setOpenRequestId] = useState<string>();
  /** The request a row belongs to, when it went to more than one firm. */
  const requestOf = useCallback(
    (row: Proposal | ProposalLoadIssue): { rfpId: string; size: number } | undefined => {
      if (!(row instanceof Proposal)) return undefined;
      const rfpId = row.RFPId?.value;
      const size = rfpId ? comparisonSizes.get(rfpId) : undefined;
      if (!rfpId || !size || size < 2) return undefined;
      return { rfpId, size };
    },
    [comparisonSizes],
  );
  // #endregion

  // Where a row goes, in one place, because the row and the link in its name cell have to agree —
  // and where a proposal opens is not a simple id. `proposalListDestination` reads what this user
  // may do with this proposal, and the section its pages are in, from the proposal itself.
  const proposalDestination = useCallback(
    (proposal: Proposal | ProposalLoadIssue): string | undefined => {
      if (isProposalLoadIssue(proposal) || !proposal.id) return undefined;
      return proposalListDestination(proposal, {
        userId: session.user?.id,
        isRestrictedOnboarding: session.isRestrictedOnboarding,
      });
    },
    [session.isRestrictedOnboarding, session.user?.id],
  );

  // Plain values rather than the object, so the column definitions below are rebuilt only when a width
  // actually changes rather than on every render.
  const {
    size: statusColumnSize,
    minSize: statusColumnMinSize,
    minimum: statusColumnMinimum,
    preferred: statusColumnPreferred,
  } = statusColumnWidth(isArchivedView, isMediumDisplaySize);
  const columns = useMemo(
    () => [
      // The server's search reads a proposal's name, its description and its fee categories' names
      // (#6590), so only the Name and Description columns mark a match; the rest leave the search.
      columnHelper.accessor(getProposalStatusFilterValue, {
        header: "Status",
        id: "status",
        enableGlobalFilter: false,
        enableSorting: false,
        // The stage chips say which statuses are listed; a second, finer status control beside them
        // offered archived statuses on lists that hold none (#6473). A link's `status` still narrows
        // the list, and shows as a criteria chip.
        enableColumnFilter: false,
        meta: { kind: "status", width: { minimum: statusColumnMinimum, preferred: statusColumnPreferred } },
        size: statusColumnSize,
        minSize: statusColumnMinSize,
        muiTableBodyCellProps: {
          sx: {
            minWidth: 0,
            overflow: "hidden",
          },
        },
        Cell: ({ table, row }) => {
          const request = isGroupedByRequest ? requestOf(row.original) : undefined;
          if (request && !table.getState().isLoading) {
            return <StatusChip label={`Sent to ${request.size} firms`} tone="info" />;
          }
          const status = renderStatusColumn(table, row.original);
          if (!isNegotiableUnderStatus || table.getState().isLoading) return status;
          if (!(row.original instanceof Proposal) || row.original.negotiable?.value !== false) return status;
          // Negotiable is the default, so only the exception is worth a line of every row.
          return (
            <StackedCell>
              {status}
              <NonNegotiableChip label="Non-negotiable" size="small" variant="outlined" />
            </StackedCell>
          );
        },
      }),
      columnHelper.accessor(getProposalNameFilterValue, {
        header: "Name",
        id: "name",
        meta: { kind: "name" },
        enableSorting: true,
        enableColumnFilter: true,
        size: proposalColumnSizes.name,
        minSize: 120,
        muiTableBodyCellProps: {
          sx: {
            minWidth: 0,
            overflow: "hidden",
          },
        },
        Cell: ({ row, table }) => {
          if (table.getState().isLoading) return;
          const value = !isProposalLoadIssue(row.original) && row.original.name?.value;
          if (!value) return <NoValueDash />;
          // The creator and the description read under the name while their own columns are hidden
          // (#6512, #6711): one line, clipped, and readable in full on hover.
          const creator = isCreatorUnderName ? creatorLabel(row.original) : undefined;
          const description = isDescriptionUnderName && !isProposalLoadIssue(row.original)
            ? row.original.description?.value
            : undefined;
          const secondary = [
            creator ? `By ${creator}` : undefined,
            description ? (
              <span key="description" data-testid="proposal-name-description">
                <SearchMatch table={table} text={description} />
              </span>
            ) : undefined,
          ];
          const name = <SearchMatch table={table} text={value} />;
          const version = (
            <WorkVersionChip version={WorkVersion.of(isProposalLoadIssue(row.original) ? undefined : row.original.workAgreement)} sx={{ marginLeft: 0 }} />
          );
          const request = requestOf(row.original);
          // A grouped row stands for every firm's proposal, so it opens the request, not one of them.
          if (isGroupedByRequest && request) {
            return (
              <CondensedCell
                data-testid="proposal-name-cell"
                title={<NameLine><RowTitle>{name}</RowTitle>{version}</NameLine>}
                secondary={secondary}
                after={<ProposalRequestFirmsLink size={request.size} onOpen={() => setOpenRequestId(request.rfpId)} />}
              />
            );
          }
          const rfpId = !isProposalLoadIssue(row.original) ? row.original.RFPId?.value : undefined;
          return (
            <CondensedCell
              data-testid="proposal-name-cell"
              title={
                <NameLine>
                  <RowTitle>
                    <RowLink to={proposalDestination(row.original)}>{name}</RowLink>
                  </RowTitle>
                  {version}
                </NameLine>
              }
              secondary={secondary}
              after={rfpId && <ProposalComparisonLink rfpId={rfpId} proposalCount={comparisonSizes.get(rfpId)} />}
            />
          );
        },
      }),
      columnHelper.accessor(getProposalRFPSortValue, {
        header: "RFP",
        id: "rfp",
        meta: {
          kind: "number",
          // The phone's cards have no column menu, so their Sort sheet offers the same grouping (#6715).
          group: { label: GROUP_BY_RFP_LABEL, isGrouped: isGroupedByRequest, onToggle: handleToggleGroupByRequest },
          fold: {
            ...proposalColumnFolds.rfp,
            render: (proposal) => {
              const request = requestOf(proposal);
              return request ? `Sent to ${request.size} firms` : null;
            },
          },
        },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        size: proposalColumnSizes.rfp,
        Header: () => <RfpHeader grouped={isGroupedByRequest} onUngroup={handleToggleGroupByRequest} />,
        // Grouping lives on the column it groups by, where a table's reader looks for it (#6601). It is
        // still the server's grouping, which groups before it pages, and not MRT's, which would group
        // only the rows on the page in hand.
        renderColumnActionsMenuItems: ({ closeMenu, internalColumnMenuItems, table }) => [
          <ActionMenuItem
            key="group-by-rfp"
            icon={<ViewAgendaOutlinedIcon />}
            label={isGroupedByRequest ? UNGROUP_BY_RFP_LABEL : GROUP_BY_RFP_LABEL}
            onClick={() => {
              handleToggleGroupByRequest();
              closeMenu();
            }}
            table={table}
          />,
          <Divider key="divider" />,
          ...internalColumnMenuItems,
        ],
        Cell: ({ row, table }) => {
          if (table.getState().isLoading) return;
          const request = requestOf(row.original);
          if (!request) return <NoValueDash />;
          return <ProposalRequestFirmsLink size={request.size} onOpen={() => setOpenRequestId(request.rfpId)} />;
        },
      }),
      columnHelper.accessor(getProposalDescriptionFilterValue, {
        header: "Description",
        id: "description",
        meta: {
          kind: "text",
          fold: {
            ...proposalColumnFolds.description,
            render: (proposal) => (isProposalLoadIssue(proposal) ? null : proposal.description?.value),
          },
        },
        enableSorting: false,
        enableColumnFilter: true,
        size: proposalColumnSizes.description,
        minSize: 120,
        Cell: DescriptionCell,
      }),
      columnHelper.display({
        header: "Creator",
        id: "creator",
        meta: {
          kind: "person",
          fold: {
            ...proposalColumnFolds.creator,
            render: (proposal) => {
              const creator = creatorLabel(proposal);
              return creator ? `By ${creator}` : null;
            },
          },
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: proposalColumnSizes.creator,
        Cell: ({ table, row }) => renderCreatorColumn(table, row.original),
      }),
      columnHelper.accessor(getProposalNegotiableFilterValue, {
        header: "Negotiable",
        id: "negotiable",
        meta: {
          kind: "status",
          fold: {
            ...proposalColumnFolds.negotiable,
            // Negotiable is the default, so only the exception is worth a line of every row.
            render: (proposal) =>
              !isProposalLoadIssue(proposal) && proposal.negotiable?.value === false ? "Non-negotiable" : null,
          },
        },
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: true,
        // A ✓ or a ✕ under "Neg.", rather than 110px for one character (#6600).
        size: proposalColumnSizes.negotiable,
        Header: NegotiableHeader,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" },
        ],
        Cell: NegotiableCell,
      }),
      columnHelper.display({
        header: "Team",
        id: "team",
        meta: { kind: "people", width: teamColumnWidth },
        enableSorting: false,
        enableColumnFilter: false,
        size: unmeasuredTeamColumnSize,
        Cell: ({ table, row, column }) => {
          const request = isGroupedByRequest ? requestOf(row.original) : undefined;
          if (request && !table.getState().isLoading) {
            return <ProposalRequestFirmsLink size={request.size} onOpen={() => setOpenRequestId(request.rfpId)} />;
          }
          return renderTeamColumn(table, row.original, column.getSize());
        },
      }),
      columnHelper.display({
        header: "Fee schedule",
        id: "feeSchedule",
        meta: {
          kind: "text",
          fold: {
            ...proposalColumnFolds.feeSchedule,
            render: (proposal) => {
              if (isGroupedByRequest && requestOf(proposal)) return null;
              return feeScheduleSummary(proposal);
            },
          },
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: proposalColumnSizes.feeSchedule,
        Cell: ({ table, row }) => {
          // Each firm quotes its own fees: the drawer and the comparison show them side by side.
          if (isGroupedByRequest && requestOf(row.original)) return <NoValueDash />;
          return renderFeeScheduleColumn(table, row.original);
        },
      }),
      // Response due and the term, in one column of two lines (#6512). It sorts from its menu, by
      // whichever of the three dates the reader picks; the single-date columns are still there,
      // hidden, and sort from their headers as before.
      columnHelper.display({
        header: "Dates",
        id: "dates",
        meta: { kind: "dateRange" },
        enableSorting: false,
        enableColumnFilter: false,
        size: proposalColumnSizes.dates,
        // One whole date, "Mar 31, 2027", and the cell's padding: a drag stops before a date is cut.
        minSize: datesColumnMinSize,
        Header: () => <DatesHeader sorting={sorting} />,
        renderColumnActionsMenuItems: ({ closeMenu, internalColumnMenuItems, table }) => [
          ...proposalDateSorts.map((dateSort) => {
            const current = sorting.find((sort) => sort.id.toLowerCase() === dateSort.id);
            // Earliest first is the natural first look at a date; a second pick turns it round.
            const desc = current ? !current.desc : false;
            return (
              <ActionMenuItem
                key={dateSort.id}
                icon={desc ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                label={`Sort by ${dateSort.label}, ${desc ? "latest" : "earliest"} first`}
                onClick={() => {
                  setSorting([{ id: dateSort.id, desc }]);
                  closeMenu();
                }}
                table={table}
              />
            );
          }),
          <Divider key="divider" />,
          ...internalColumnMenuItems,
        ],
        Cell: DatesCell,
      }),
      columnHelper.accessor(getProposalResponseDueSortValue, {
        header: "Response due",
        id: "responseDueBy",
        meta: { kind: "date", fold: { ...proposalColumnFolds.responseDueBy, render: foldedDate("Due", (proposal) => proposal.responseDueBy) } },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        size: proposalColumnSizes.responseDueBy,
        Cell: ResponseDueByCell,
      }),
      columnHelper.accessor(getProposalStartDateSortValue, {
        header: "Start date",
        id: "start",
        meta: { kind: "date", fold: { ...proposalColumnFolds.start, render: foldedDate("Starts", (proposal) => proposal.startDate) } },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        size: proposalColumnSizes.start,
        Cell: StartDateCell,
      }),
      columnHelper.accessor(getProposalEndDateSortValue, {
        header: "End date",
        id: "end",
        meta: { kind: "date", fold: { ...proposalColumnFolds.end, render: foldedDate("Ends", (proposal) => proposal.endDate) } },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        size: proposalColumnSizes.end,
        Cell: EndDateCell,
      }),
      columnHelper.display({
        header: "Hired",
        id: "hireDate",
        meta: { kind: "date", fold: { ...proposalColumnFolds.hireDate, render: foldedDate("Hired", (proposal) => proposal.hireDate) } },
        enableSorting: false,
        enableColumnFilter: false,
        size: proposalColumnSizes.hireDate,
        Cell: HireDateCell,
      }),
      columnHelper.accessor(getProposalArchiveDateSortValue, {
        header: "Archived",
        id: "deleted",
        meta: { kind: "date", fold: { ...proposalColumnFolds.deleted, render: foldedDate("Archived", (proposal) => proposal.archiveDate) } },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        size: proposalColumnSizes.deleted,
        Cell: ArchiveDateCell,
      }),
    ],
    [
      comparisonSizes,
      handleToggleGroupByRequest,
      isGroupedByRequest,
      proposalDestination,
      renderStatusColumn,
      requestOf,
      renderCreatorColumn,
      renderTeamColumn,
      renderFeeScheduleColumn,
      creatorLabel,
      isCreatorUnderName,
      isDescriptionUnderName,
      isNegotiableUnderStatus,
      setOpenRequestId,
      setSorting,
      sorting,
      statusColumnMinimum,
      statusColumnMinSize,
      statusColumnPreferred,
      statusColumnSize,
      unmeasuredTeamColumnSize,
    ],
  );

  const openProposal = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal)) {
        console.warn("Cannot open proposal with loading issues.");
        return;
      }
      const destination = proposalDestination(proposal);
      if (!destination) {
        console.error("Proposal ID is required");
        return;
      }
      navigate(destination);
    },
    [navigate, proposalDestination],
  );

  /** A row opens its proposal, or, grouped, the request it stands for. */
  const openRow = useCallback(
    (row: Proposal | ProposalLoadIssue) => {
      const request = isGroupedByRequest ? requestOf(row) : undefined;
      if (request) setOpenRequestId(request.rfpId);
      else openProposal(row);
    },
    [isGroupedByRequest, openProposal, requestOf, setOpenRequestId],
  );

  const tabStrip = useMemo(() => session.isRestrictedOnboarding ? undefined : (
    <ListPageTabs
      value={effectivePageTab}
      onChange={handlePageTabChange}
      tabs={[
        {
          value: PageTab.Proposals,
          label: PageTab.Proposals,
          counts: "open proposal(s)",
          count: counts?.open,
          isLoading: listCounts.isLoading,
          badgeColor: "info",
        },
        {
          value: PageTab.Templates,
          label: PageTab.Templates,
          counts: "proposal template(s)",
          count: templateCount,
          isLoading: proposalTemplatesQuery.isLoading,
        },
      ]}
    />
  ), [
    effectivePageTab,
    handlePageTabChange,
    counts?.open,
    listCounts.isLoading,
    proposalTemplatesQuery.isLoading,
    session.isRestrictedOnboarding,
    templateCount,
  ]);

  const renderTopToolbarCustomActions = useCallback(({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
    <ListPageHeading
      title="Proposals"
      titleAction={
        isMediumDisplaySize && !session.isRestrictedOnboarding ? (
          <CreateProposalSplitButton
            compact
            canCreate={canCreateProposal}
            disabledReason={createProposalTooltip}
            onCreate={() => navigate("/proposals/drafts/create")}
            onCreateFromPrevious={() => setIsCreateFromPreviousOpen(true)}
            onCreateFromTemplate={() => setIsCreateFromTemplateOpen(true)}
          />
        ) : undefined
      }
      tabs={tabStrip}
      criteria={
        <WorkRecordCriteriaChips
          criteria={WorkRecordCriteria.PROPOSALS}
          searchParams={searchParams}
          onChange={(next) => {
            remember(`${location.pathname}?${next.toString()}`);
            setSearchParams(next);
          }}
          shownElsewhere={PROPOSAL_CRITERIA_SHOWN_ELSEWHERE}
        />
      }
      chips={
        session.isRestrictedOnboarding ? undefined : (
          <>
            <ListChips
              groups={proposalChipGroups}
              selection={chips}
              counts={chipCounts}
              onChange={handleChipsChange}
              chipIcon={proposalChipIcon}
            />
            {/* Grouping is chosen from the RFP column (#6601); while it is on, this says so beside the
                filters and undoes it in one press. */}
            {isGroupedByRequest && (
              <Chip
                icon={<ViewAgendaOutlinedIcon />}
                label={GROUPED_BY_RFP_LABEL}
                color="primary"
                onDelete={handleToggleGroupByRequest}
              />
            )}
          </>
        )
      }
      // Room beside the tabs for the Views button as well as the search and the tools (#6717).
      mobileToolbarMinWidth={NARROW_TOOLBAR_MIN_WIDTH + VIEWS_BUTTON_MAX_WIDTH + 8}
      mobileToolbar={
        isMediumDisplaySize ? (
          // On a phone the Views button is its star alone, so it shares the row and the search keeps
          // its width (#6720); it had a line of its own, beside a search left a bare icon (#6717).
          <ListPageMobileToolbar>
            <ViewsMenu views={tableViews} table={table} defaultColumnVisibility={defaultColumnVisibility} />
            <ListPageMobileSearch table={table} />
            <TableToolsMenu table={table} />
          </ListPageMobileToolbar>
        ) : undefined
      }
    />
  ), [
    canCreateProposal,
    chipCounts,
    chips,
    createProposalTooltip,
    defaultColumnVisibility,
    handleChipsChange,
    handleToggleGroupByRequest,
    isGroupedByRequest,
    isMediumDisplaySize,
    location.pathname,
    navigate,
    remember,
    searchParams,
    session.isRestrictedOnboarding,
    setIsCreateFromPreviousOpen,
    setIsCreateFromTemplateOpen,
    setSearchParams,
    tableViews,
    tabStrip,
  ]);

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ListPageToolbarButtons>
            <ToolbarButtons>
              <ViewsMenu views={tableViews} table={props.table} defaultColumnVisibility={defaultColumnVisibility} />
              <GlobalFilterTextField table={props.table} sx={searchFieldSpacing} />
              <TableToolsMenu table={props.table} />
            </ToolbarButtons>
            <CreateProposalSplitButton
              compact={false}
              canCreate={!session.isRestrictedOnboarding && canCreateProposal}
              disabledReason={createProposalTooltip}
              onCreate={() => navigate("/proposals/drafts/create")}
              onCreateFromPrevious={() => setIsCreateFromPreviousOpen(true)}
              onCreateFromTemplate={() => setIsCreateFromTemplateOpen(true)}
            />
          </ListPageToolbarButtons>
        )}
      </>
    ),
    [
      canCreateProposal,
      createProposalTooltip,
      defaultColumnVisibility,
      isMediumDisplaySize,
      navigate,
      session.isRestrictedOnboarding,
      setIsCreateFromPreviousOpen,
      setIsCreateFromTemplateOpen,
      tableViews,
    ],
  );

  const isPreparingProposalsQuery =
    !sessionPermissions || (Boolean(session.user?.legalEntityId) && !proposalsQueryParams);
  const isTableLoading = isPreparingProposalsQuery || proposalsAPIQuery.isFetching;
  const showTableSkeletons = !tableProposals && isTableLoading;
  const skeletonProposals = useMemo(
    () =>
      Array.from({ length: pagination.pageSize }, (_, index) => ({
        id: Guid.generate(),
        issue: `Loading proposal ${index}`,
      })),
    [pagination.pageSize],
  );
  const tableData = useMemo(
    () => (showTableSkeletons ? skeletonProposals : (tableProposals ?? [])),
    [showTableSkeletons, skeletonProposals, tableProposals],
  );
  const tableRowCount = getTableRowCount({
    headers: tableProposalsHeaders,
    isShowingSkeletons: showTableSkeletons,
    pageSize: pagination.pageSize,
    tableProposalCount: tableProposals?.length,
  });

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => {
      const isLoadIssueRow = !showTableSkeletons && isProposalLoadIssue(row.original);
      const proposalTableContext =
        !showTableSkeletons && !isProposalLoadIssue(row.original)
          ? getProposalTableContext(row.original)
          : undefined;
      const rowIdValue = row.original.id?.value;
      const isRefused = rowIdValue !== undefined && refusedDeletions[rowIdValue] !== undefined;
      const isNewlyRefused = isRefused && prevRefusedDeletions[rowIdValue] === undefined;

      return {
        "data-proposal-table-context": proposalTableContext,
        onClick: showTableSkeletons ? undefined : () => openRow(row.original),
        sx: (theme: Theme) => {
          const contextRowStyles = getProposalTableContextRowStyles(theme, proposalTableContext);
          return {
            ...contextRowStyles,
            backgroundColor: isLoadIssueRow
              ? "rgba(255, 100, 100, 0.1)"
              : (contextRowStyles.backgroundColor ?? "inherit"),
            cursor: showTableSkeletons || isLoadIssueRow ? "default" : "pointer",
            animation:
              !showTableSkeletons && isNewlyRefused
                ? `${failedOperationKeyFrame} 1s ease-in-out`
                : "none",
            animationIterationCount: isRefused ? "3" : "0",
          };
        },
      };
    },
    [openRow, prevRefusedDeletions, refusedDeletions, showTableSkeletons],
  );

  // The ⋯ that ends every row, with Open first (#6711): the third way into a row, beside the name drawn
  // as a link and the row's own click. It opens what the row and the name open.
  const renderRowActions = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => {
      if (showTableSkeletons) return null;
      const request = isGroupedByRequest ? requestOf(row.original) : undefined;
      const destination = proposalDestination(row.original);
      let open: RowOpenAction | undefined;
      if (request) open = { label: "Open request", onClick: () => setOpenRequestId(request.rfpId) };
      else if (destination) open = { to: destination };
      const name = isProposalLoadIssue(row.original) ? undefined : row.original.name?.value;
      return <RowActions actions={[]} open={open} ariaLabel={name ? `Actions for ${name}` : undefined} />;
    },
    [isGroupedByRequest, proposalDestination, requestOf, setOpenRequestId, showTableSkeletons],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (proposalsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    return {
      action: (
        <AlertActions>
          <Button color="error" startIcon={<DeleteIcon />} onClick={handleBulkDraftProposalDelete}>
            Delete
          </Button>
        </AlertActions>
      ),
      sx: (theme: Theme) => ({
        "& .MuiBox-root .MuiStack-root": {
          alignItems: "baseline",
          gap: theme.spacing(1),
        },
      }),
    };
  }, [proposalsAPIQuery.isError, handleBulkDraftProposalDelete]);

  const getRowId = useCallback((row: Proposal | ProposalLoadIssue) => {
    if (isProposalLoadIssue(row)) return `load-issue-${row.id.value}`;
    return row.id?.value ?? "";
  }, []);

  // Why a row cannot be picked for deletion, or undefined when it can. Only a draft's creator may
  // delete it, and the server says so per draft in `availableActions`, so the list offers only what
  // the reader can do, and a refused row says why rather than failing after the confirm (#6577).
  const deletionWithheldReason = useCallback(
    (row: Proposal | ProposalLoadIssue): string | undefined => {
      if (isProposalLoadIssue(row) || !row.id) return "This proposal could not be loaded";
      const refusal = refusedDeletions[row.id.value];
      if (refusal) return draftDeletionRefusalLabel(refusal);
      if (row.isDeletableBy(session.user?.id)) return undefined;
      const creatorUserId = row.creator?.userId;
      const creator = cachedCreators.find((individual) => individual.userId.isEqualTo(creatorUserId));
      return draftDeletionWithheldReason(creator?.fullName);
    },
    [cachedCreators, refusedDeletions, session.user?.id],
  );

  const muiSelectCheckboxProps = useCallback(
    (props: { row: MRT_Row<Proposal | ProposalLoadIssue> }): CheckboxProps => ({
      title: selectionWithheldReason ?? deletionWithheldReason(props.row.original),
    }),
    [deletionWithheldReason, selectionWithheldReason],
  );

  const tableOptions = useMemo<MRT_TableOptions<Proposal | ProposalLoadIssue>>(() => {
    return {
      columns,
      data: tableData,
      getRowId,
      enableRowSelection: hasSelectColumn
        ? (row) =>
            !showTableSkeletons &&
            selectionWithheldReason === undefined &&
            deletionWithheldReason(row.original) === undefined
        : false,
      muiSelectCheckboxProps,
      muiSelectAllCheckboxProps: { disabled: selectionWithheldReason !== undefined, title: selectionWithheldReason },
      enableRowActions: true,
      renderRowActions,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      enableColumnPinning: true,
      initialState: {
        columnOrder: proposalColumnOrder,
        columnPinning: { right: ["mrt-row-actions"] },
        showGlobalFilter: true,
      },
      manualFiltering: true,
      enableColumnFilters: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps: {
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative"
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden"
        },
      },
      muiTopToolbarProps: {
        sx: {
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          "& > .MuiBox-root": {
            gap: 0,
            minWidth: 0,
            overflow: "hidden",
          },
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: `Search ${listSubject}`,
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: globalFilter ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setGlobalFilter("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableRowCount,
      pageCount: Math.ceil(tableRowCount / pagination.pageSize),
      enablePagination: true,
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: proposalsAPIQuery.isError,
        isLoading: isTableLoading,
        onRetry: () => void proposalsAPIQuery.refetch(),
        // The chip is the noun: "No drafts" would be the chip's name, not the thing it holds.
        subject: listSubject,
      }),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      onColumnVisibilityChange: setUserColumnVisibility,
      positionGlobalFilter: "none",
      state: {
        columnVisibility,
        columnFilters,
        globalFilter,
        isLoading: isTableLoading,
        pagination,
        showAlertBanner: Boolean(proposalsAPIQuery.isError),
        showGlobalFilter: true,
        showProgressBars: proposalsAPIQuery.isFetching,
        sorting,
        rowSelection,
        showSkeletons: showTableSkeletons,
      },
    };
  }, [
    columns, 
    tableData, 
    tableRowCount,
    listSubject,
    hasSelectColumn,
    selectionWithheldReason,
    columnVisibility,
    setUserColumnVisibility,
    muiSelectCheckboxProps, 
    muiTableBodyRowProps, 
    renderRowActions,
    renderToolbarInternalActions, 
    renderTopToolbarCustomActions,
    muiToolbarAlertBannerProps, 
    pagination, 
    columnFilters, 
    globalFilter, 
    isTableLoading, 
    proposalsAPIQuery, 
    sorting, 
    rowSelection, 
    deletionWithheldReason, 
    getRowId, 
    showTableSkeletons, 
    setColumnFilters, 
    setGlobalFilter, 
    setPagination, 
    setRowSelection,
    setSorting
  ]);

  const onboardingState = session.onboardingWork?.state;
  if (
    session.isRestrictedOnboarding &&
    onboardingState &&
    onboardingState !== "AgreementRequired" &&
    onboardingState !== "ProposalInProgress" &&
    onboardingState !== "Completed"
  ) {
    return <OnboardingProposalStatePage state={onboardingState} />;
  }

  if (!sessionPermissions) return <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} columnChoice={userColumnVisibility} tableKey="proposals" cards />;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;
  if (isTemplatesTab) return <ProposalTemplatesTable tabs={tabStrip} />;

  return (
    <>
      {/* Above the table, which fills the page: below it, the section is out of sight. */}
      {isArchivedView && <ProposalsYouLeft />}
      <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} columnChoice={userColumnVisibility} tableKey="proposals" cards />
      <ProposalRequestDrawer
        rfpId={openRequestId}
        onClose={() => setOpenRequestId(undefined)}
      />
      <CreateFromPreviousProposalDialog
        open={isCreateFromPreviousOpen}
        onClose={() => setIsCreateFromPreviousOpen(false)}
      />
      <CreateFromProposalTemplateDialog
        open={isCreateFromTemplateOpen}
        onClose={() => setIsCreateFromTemplateOpen(false)}
      />
    </>
  );
}

/** The drafts a bulk delete refused, by id value, with the reason the server gave for each. */
type RefusedDeletions = Partial<Record<string, DraftDeletionRefusal>>;

/**
 * Asks the server to delete `ids`, and answers every id: deleted, or refused with a reason. A
 * request whose drafts the reader created none of is a 403 rather than a 207 (#6577), and it carries
 * no body to read, so its refusals are all `unauthorized`. Any other failure answers undefined.
 *
 * Module-level so the `try` stays out of the component the React Compiler compiles.
 */
async function deleteDrafts(
  deleteQuery: ReturnType<typeof useBulkDeleteProposalsMutation>[0],
  ids: string[],
): Promise<DraftDeletionResultJSON | undefined> {
  try {
    return await deleteQuery({ ids }).unwrap();
  } catch (error) {
    if (isFetchBaseQueryError(error) && error.status === HttpStatusCode.Forbidden) {
      return { deleted: [], unavailable: ids.map((id) => ({ id, reason: DraftDeletionRefusal.Unauthorized })) };
    }
    console.error(error);
    showErrorSnackbar(error, { doing: "deleting your drafts", operation: "proposals.delete-drafts" });
    return undefined;
  }
}

function getGlobalFilterFromSearchParams(searchParams: URLSearchParams): string {
  return searchParams.get("query") ?? "";
}

/** The Negotiable column's filter, under the name the API binds. */
const NEGOTIABLE_PARAMETER = "isNegotiable";

/**
 * The criteria the table's own controls write: the search box and the column filters. The table
 * keeps or clears these itself; every other criterion in the URL came from a link and is kept.
 */
const PROPOSAL_TABLE_PARAMETERS: readonly string[] = ["query", "name", "description", NEGOTIABLE_PARAMETER];

/** The search box shows `query`, so it gets no chip of its own. */
const PROPOSAL_CRITERIA_SHOWN_ELSEWHERE: readonly string[] = ["query"];

// While it is loading with no data, material-react-table fills the body with placeholder rows of
// its own: plain objects holding `null` under every column id. They carry no `issue` key, so
// "not a load issue" does not mean "a Proposal" — asking that let `status.toString()` read null and
// take the whole page down on a refetch that started from an empty result. Every accessor asks
// whether the row is a Proposal instead, and anything else sorts and filters as empty.
function readProposalColumnValue(
  row: Proposal | ProposalLoadIssue,
  read: (proposal: Proposal) => string | undefined,
): string {
  return row instanceof Proposal ? (read(row) ?? "") : "";
}

function getProposalStatusFilterValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.status.toString());
}

function getProposalNameFilterValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.name?.value);
}

function getProposalDescriptionFilterValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.description?.toString());
}

function getProposalNegotiableFilterValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.negotiable.toString());
}

function getProposalResponseDueSortValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.responseDueBy?.toString());
}

function getProposalStartDateSortValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.startDate?.toString());
}

function getProposalEndDateSortValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.endDate?.toString());
}

function getProposalArchiveDateSortValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.archiveDate?.toString());
}

function getProposalRFPSortValue(row: Proposal | ProposalLoadIssue): string {
  return readProposalColumnValue(row, (proposal) => proposal.RFPId?.value);
}

/** Opens the drawer of a request's proposals (#6460). The row underneath must not open as well. */
function ProposalRequestFirmsLink(props: Readonly<{ size: number; onOpen: () => void }>) {
  const { size, onOpen } = props;
  function handleClick(event: MouseEvent) {
    event.stopPropagation();
    onOpen();
  }
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={handleClick}
      sx={{ alignSelf: "flex-start", textAlign: "left", whiteSpace: "nowrap" }}
    >
      {size} firms
    </Link>
  );
}

function getColumnFiltersFromSearchParams(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  const name = searchParams.get("name");
  const description = searchParams.get("description");
  const negotiable = searchParams.get(NEGOTIABLE_PARAMETER) ?? searchParams.get(LEGACY_NEGOTIABLE_PARAMETER);

  if (name) filters.push({ id: "name", value: name });
  if (description) filters.push({ id: "description", value: description });
  if (negotiable !== null) filters.push({ id: "negotiable", value: negotiable });

  return filters;
}

function getSortingFromSearchParams(searchParams: URLSearchParams): SortingState {
  const orderByParam = searchParams.get("orderBy")?.toLowerCase();
  const orderBy = Object.values(ProposalOrderField).find((field) => field.toLowerCase() === orderByParam);
  if (!orderBy) return [];

  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir = Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam);

  return [{
    id: orderBy.toLowerCase(),
    desc: orderDir !== OrderDirection.Ascending,
  }];
}

function getPaginationFromSearchParams(searchParams: URLSearchParams): PaginationState {
  const { pageIndex, pageSize } = PaginationParameters.fromListPageSearchParams(searchParams);
  return { pageIndex, pageSize };
}

function getSearchParamsFromTableState(
  state: ProposalTableSearchState,
  currentSearchParams: URLSearchParams,
): URLSearchParams {
  const { globalFilter, columnFilters, sorting, pagination } = state;
  const newSearchParams = new URLSearchParams();
  if (globalFilter) {
    newSearchParams.set("query", globalFilter);
  }

  const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
  if (nameFilter) {
    newSearchParams.set("name", nameFilter);
  }

  const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
  if (descriptionFilter) {
    newSearchParams.set("description", descriptionFilter);
  }

  const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
  if (negotiableFilter !== undefined) {
    newSearchParams.set(NEGOTIABLE_PARAMETER, negotiableFilter);
  }

  const orderingField = sorting.find((sort) =>
    Object.values(ProposalOrderField)
      .map((field) => field.toLowerCase())
      .includes(sort.id.toLowerCase()),
  );

  if (orderingField) {
    const orderBy = Object.values(ProposalOrderField).find(
      (field) => field.toLowerCase() === orderingField.id.toLowerCase(),
    );
    if (orderBy) newSearchParams.set("orderBy", orderBy);
    newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  const paginationPageIndex = pagination.pageIndex ?? 0;
  newSearchParams.set("pageIndex", paginationPageIndex.toString());
  const paginationPageSize = pagination.pageSize ?? PaginationParameters.DEFAULT_PAGE_SIZE;
  newSearchParams.set("pageSize", paginationPageSize.toString());

  // The chips are the page's own choice, not the table's, so a change to the table keeps them.
  for (const parameter of [STAGE_PARAMETER, MOVE_PARAMETER, OUTCOME_PARAMETER]) {
    for (const value of currentSearchParams.getAll(parameter)) newSearchParams.append(parameter, value);
  }
  if (currentSearchParams.get(GROUP_BY_REQUEST_PARAMETER)?.toLowerCase() === "true") {
    newSearchParams.set(GROUP_BY_REQUEST_PARAMETER, "true");
  }

  return WorkRecordCriteria.PROPOSALS.carryOver(currentSearchParams, newSearchParams, PROPOSAL_TABLE_PARAMETERS);
}

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

function getTableRowCount({
  headers,
  isShowingSkeletons,
  pageSize,
  tableProposalCount,
}: {
  headers?: PaginationHeaders;
  isShowingSkeletons: boolean;
  pageSize: number;
  tableProposalCount?: number;
}): number {
  if (isShowingSkeletons) return pageSize;
  return headers?.totalElements ?? tableProposalCount ?? 0;
}

/**
 * The description, clipped to its column, and readable in full on hover (GitHub issue #5605).
 *
 * A description is prose and the column is 200px, so almost every row ends in an ellipsis and the
 * reader has no way to read the rest of it from the table. `TruncatedCell` is the same answer the
 * communications table's Description column got in #5130: it reveals the whole value on hover, and
 * only when the cell measured a clip, so a short description gains no tooltip over text the reader
 * can already see.
 */
const DescriptionCell = ({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.description?.value;
  if (!value) return <NoValueDash />;
  return (
    <TruncatedCell>
      <SearchMatch table={table} text={value} />
    </TruncatedCell>
  );
};

const NegotiableCell = ({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  let value: boolean | undefined;
  if (!isProposalLoadIssue(row.original)) {
    value = row.original.negotiable?.value;
  }
  if (value === undefined) {
    return <NoValueDash />;
  }
  // The column's header is "Neg." to the eye, so each mark says in full what it answers.
  return value
    ? <CheckIcon fontSize="small" titleAccess="Negotiable: yes" />
    : <CloseIcon fontSize="small" titleAccess="Negotiable: no" />;
};

/** "Neg." to the eye, with the column's whole name on hover and to a screen reader (#6600). */
function NegotiableHeader() {
  return (
    <Tooltip describeChild title="Negotiable">
      <span>
        <span aria-hidden="true">{compactHeaderLabels.negotiable}</span>
        <Box component="span" sx={visuallyHidden}>Negotiable</Box>
      </span>
    </Tooltip>
  );
}

function ResponseDueByCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.responseDueBy;
  return <DateCell value={value || undefined} />;
}

function StartDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.startDate;
  return <DateCell value={value || undefined} />;
}

function EndDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.endDate;
  return <DateCell value={value || undefined} />;
}

/**
 * The Dates column: when a response is due, over the term the work runs.
 *
 * A term that crosses into another year says both years and, with the roomier padding of a wide
 * window, does not fit the column on one line. Its two sides are flex items that wrap after the dash
 * whatever `white-space` the cell sets — a compact cell sets `nowrap`, which kept plain inline text on
 * one line and clipped it — so no date is cut in half. The column cannot be dragged narrower than
 * one date, and anything still clipped reads in full on hover, as the due date does.
 */
function DatesCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  if (isProposalLoadIssue(row.original)) return <NoValueDash />;
  const { due, term } = summarizeProposalDates(
    row.original.responseDueBy,
    row.original.startDate,
    row.original.endDate,
  );
  if (!due && !term) return <NoValueDash />;
  return (
    <StackedCell data-testid="proposal-dates">
      {due && <TruncatedCell>{due}</TruncatedCell>}
      {term && (
        <TruncatedCell title={term}>
          <TermSides data-testid="proposal-term">
            {term.split(" – ").map((side, index, sides) => (
              <span key={side}>{index < sides.length - 1 ? `${side} –` : side}</span>
            ))}
          </TermSides>
        </TruncatedCell>
      )}
    </StackedCell>
  );
}

/**
 * "Dates", and the direction of the sort when the table is sorted by one of them. The column sorts
 * from its menu rather than its header, so MRT draws no sort arrow of its own here.
 */
export const GROUP_BY_RFP_LABEL = "Group by RFP";
export const UNGROUP_BY_RFP_LABEL = "Ungroup";
export const GROUPED_BY_RFP_LABEL = "Grouped by RFP";

/**
 * "RFP", and while the list is grouped by it, the group icon beside it: pressed, it ungroups (#6601).
 * The press stops at the icon, so it does not also sort the column whose header it sits in.
 */
function RfpHeader(props: Readonly<{ grouped: boolean; onUngroup: () => void }>) {
  if (!props.grouped) return <>RFP</>;
  return (
    <Box component="span" sx={{ alignItems: "center", display: "inline-flex", gap: 0.5 }}>
      RFP
      <Tooltip title={GROUPED_BY_RFP_LABEL}>
        <IconButton
          aria-label={`${GROUPED_BY_RFP_LABEL}. ${UNGROUP_BY_RFP_LABEL}`}
          size="small"
          sx={{ color: "primary.main", p: 0.25 }}
          onClick={(event) => {
            event.stopPropagation();
            props.onUngroup();
          }}
        >
          <ViewAgendaOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function DatesHeader(props: Readonly<{ sorting: SortingState }>) {
  const sort = props.sorting.find((candidate) =>
    proposalDateSorts.some((dateSort) => dateSort.id === candidate.id.toLowerCase()),
  );
  const dateSort = sort && proposalDateSorts.find((candidate) => candidate.id === sort.id.toLowerCase());
  if (!sort || !dateSort) return <>Dates</>;
  const description = `Sorted by ${dateSort.label}, ${sort.desc ? "latest" : "earliest"} first`;
  const SortIcon = sort.desc ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Box component="span" sx={{ alignItems: "center", display: "inline-flex", gap: 0.5 }}>
      Dates
      <Tooltip title={description}>
        <SortIcon aria-label={description} fontSize="small" sx={{ color: "text.secondary" }} />
      </Tooltip>
    </Box>
  );
}

/** A date column's value as it reads folded under Dates: "Hired Mar 3, 2027". */
function foldedDate(label: string, read: (proposal: Proposal) => AHDate | null | undefined) {
  return (proposal: Proposal | ProposalLoadIssue): string | null => {
    if (isProposalLoadIssue(proposal)) return null;
    const value = formatDate(read(proposal));
    return value ? `${label} ${value}` : null;
  };
}

/** A proposal's fee schedule as it reads folded under the name: "3 fees". */
function feeScheduleSummary(proposal: Proposal | ProposalLoadIssue): string | null {
  if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return null;
  if (Masked.isMasked(proposal.feeSchedule)) return "Fee schedule (masked)";
  const feeCount = proposal.feeSchedule.length;
  if (feeCount === 0) return null;
  return `${feeCount} ${feeCount === 1 ? "fee" : "fees"}`;
}

function HireDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.hireDate;
  return <DateCell value={value || undefined} />;
}

function ArchiveDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.archiveDate;
  return <DateCell value={value || undefined} />;
}
